/**
 * ============================================================================
 * 🎯 SINGLE SOURCE OF TRUTH FOR ALL DATE & TIME FORMATTING
 * ============================================================================
 * 
 * ⚠️ CRITICAL: This is the ONLY file that should be used for date/time formatting
 * 
 * DO NOT:
 * - Create inline date formatting (new Date().toLocaleDateString())
 * - Create duplicate formatter functions in other files
 * - Import from deprecated files (timeFormatter.ts, common/formatters.ts)
 * - Use Date constructor directly for date-only values (causes timezone bugs!)
 * 
 * ALWAYS:
 * - Import all date/time formatting from this file
 * - Use parseDateLocal() for date-only values (YYYY-MM-DD format)
 * - Follow the date/time rules in the scheduling binder
 *   → docs/agent-context/classes-scheduling.md
 * 
 * KEY PRINCIPLES:
 * ✅ Timezone-Safe: All date parsing uses parseDateLocal() to prevent "June 1 → May 31" bugs
 * ✅ DRY Compliance: Single implementation of each formatter function
 * ✅ Consistent: Same formatting patterns across entire application
 * ✅ Documented: JSDoc comments explain usage and provide examples
 * 
 * MIGRATION PATH:
 * If you're using date/time formatting from other files, migrate to this file:
 * 
 * OLD (deprecated):
 *   import { formatDate } from '@/utils/common/formatters'
 *   import { formatTimeTo12Hour } from '@/utils/formatters/timeFormatter'
 * 
 * NEW (correct):
 *   import { formatDate, formatTimeTo12Hour } from '@/utils/formatters/dateUtils'
 * 
 * AVAILABLE FUNCTIONS:
 * - parseDateLocal() - Parse YYYY-MM-DD without timezone conversion
 * - formatDate() - Display dates correctly
 * - formatDateWithDay() - Date with day of week
 * - formatDateRange() - Format date ranges
 * - formatTime() - Format timestamps to time
 * - formatTimeTo12Hour() - Format HH:MM:SS to 12-hour format
 * - timeToMinutes() - Convert HH:MM:SS to minutes (for calculations)
 * - minutesToTime() - Convert minutes to HH:MM:SS (for calculations)
 * - timesOverlap() - Check if two time ranges overlap
 * - formatDateTime() - Combined date and time
 * - calculateDuration() - Calculate days/months between dates
 * - formatDuration() - Display duration as "X days (approximately Y months)"
 * - daysSinceDate() - Calendar days between today and a date (positive = past)
 * - isDatePast() - Is a date-only string strictly before today?
 * - daysBetweenDates() - Calendar days between two date-only strings
 * - formatRelativeTime() - "5 minutes ago", "in 3 days"
 * - formatExpirationTime() - Countdown to expiration
 * - ordinalDay() - Format day as 1st, 2nd, 3rd, etc.
 * - BILLING_DAY_OPTIONS - Standard 1-28 billing days
 * 
 * RELATED FILES:
 * - Recurring Event Logic: @/utils/schedule/recurringEvents (RRULE parsing, event expansion)
 * - Deprecated Files: timeFormatter.ts, common/formatters.ts (do not use)
 * 
 * Last Updated: 2025-10-17
 * ============================================================================
 */

export const formatRelativeTime = (dateString: string | undefined | null): string => {
  if (!dateString) return 'Never';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  } else if (diffInWeeks < 4) {
    return `${diffInWeeks}w ago`;
  } else if (diffInMonths < 12) {
    return `${diffInMonths}mo ago`;
  } else {
    return `${diffInYears}y ago`;
  }
};

// ============================================================================
// CORE TIMEZONE-SAFE FUNCTIONS (Use these for date-only values!)
// ============================================================================

/**
 * Parse a date string (YYYY-MM-DD) as a local date without timezone conversion
 * This prevents the common bug where "2025-06-01" displays as "May 31, 2025"
 * 
 * ⚠️ CRITICAL: Use this instead of new Date(dateString) for date-only values!
 * 
 * Handles multiple formats:
 * - Plain date: "2025-06-01"
 * - ISO timestamp: "2025-06-01T00:00:00.000Z"
 * - Postgres timestamp: "2025-06-01 00:00:00+00"
 * 
 * @param dateString - Date string in various formats
 * @returns Date object in local timezone
 * 
 * @example
 * // ❌ WRONG - causes timezone bug
 * const date = new Date("2025-06-01"); // Shows as May 31 in some timezones!
 * 
 * // ✅ CORRECT - timezone-safe
 * const date = parseDateLocal("2025-06-01"); // Always June 1
 * const date = parseDateLocal("2025-06-01 00:00:00+00"); // Also June 1
 */
export const parseDateLocal = (dateString: string): Date => {
  // Extract just the date portion (YYYY-MM-DD) from various formats
  // Handles: "2025-06-01", "2025-06-01T00:00:00Z", "2025-06-01 00:00:00+00"
  const dateOnly = dateString.split(/[T\s]/)[0]; // Split on 'T' or space
  const [year, month, day] = dateOnly.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

/**
 * Format a Date object as YYYY-MM-DD (plain date, no timezone)
 * Use this when saving date-only values to the database
 * 
 * ⚠️ CRITICAL: Use this instead of .toISOString().split('T')[0] for date-only values!
 * 
 * @param date - Date object
 * @returns Date string in YYYY-MM-DD format
 * 
 * @example
 * // ❌ WRONG - causes timezone bug
 * const dateStr = new Date(2025, 5, 1).toISOString().split('T')[0]; // "2025-05-31" in some timezones!
 * 
 * // ✅ CORRECT - timezone-safe
 * const dateStr = formatDateOnly(new Date(2025, 5, 1)); // "2025-06-01" always
 */
export const formatDateOnly = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a Date's calendar date (YYYY-MM-DD) as it falls in a specific IANA
 * timezone. This is the timezone-aware sibling of formatDateOnly() (which reads
 * runtime-local getters). When no timezone is supplied it degrades to
 * formatDateOnly, preserving the caller's previous runtime-local behavior.
 *
 * ⚠️ Use this — not formatDateOnly — whenever a date key must agree with a key
 * written on another machine/timezone. The recurring-event EXDATE match is the
 * canonical case: the delete/edit WRITE side computes the exclusion key in the
 * event's own timezone (eventDeletionRules.appendExdateToRecurrenceRule →
 * formatRRuleDateInTimeZone), so the calendar EXPANSION must read it back in
 * that same timezone. Matching in runtime-local instead let a 9pm-ET occurrence
 * (…T01:00:00Z) be keyed "06-09" under a UTC runtime while the EXDATE said
 * "06-08", so a deleted evening class kept showing (issue #1548). Both sides
 * extract Y/M/D via the identical Intl.DateTimeFormat config below.
 *
 * @param date - Date instant to key
 * @param timeZone - IANA timezone (e.g. "America/New_York"); falsy → runtime-local
 * @returns Date string in YYYY-MM-DD format
 */
export const formatDateOnlyInTimeZone = (date: Date, timeZone?: string | null): string => {
  if (!timeZone) return formatDateOnly(date);
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    return year && month && day ? `${year}-${month}-${day}` : formatDateOnly(date);
  } catch {
    // Invalid timezone string (e.g. "CST", garbage data) — fall back to
    // runtime-local instead of throwing (mirrors formatTimeInTimeZone).
    return formatDateOnly(date);
  }
};

/**
 * Get the number of calendar days between today and a date-only string.
 * Positive = date is in the past (overdue), Negative = date is in the future.
 *
 * Both sides are normalized to local-midnight so timezone offsets never
 * add a phantom extra day.
 *
 * @param dateString - Date string (YYYY-MM-DD or any format parseDateLocal handles)
 * @returns Integer number of days (positive = past, negative = future, 0 = today)
 *
 * @example
 * // Today is March 15
 * daysSinceDate("2026-03-04") // 11  (not 12!)
 * daysSinceDate("2026-03-20") // -5
 * daysSinceDate("2026-03-15") // 0
 */
export const daysSinceDate = (dateString: string): number => {
  const target = parseDateLocal(dateString);
  const now = new Date();
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // Math.round handles DST transitions where midnight-to-midnight is 23 or 25 hours
  return Math.round((todayLocal.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * Check if a date-only string is strictly before today (i.e., the date has passed).
 * Timezone-safe — compares calendar dates only, no time-of-day influence.
 *
 * @param dateString - Date string (YYYY-MM-DD or any format parseDateLocal handles)
 * @returns true if the date is before today
 *
 * @example
 * // Today is March 15
 * isDatePast("2026-03-14") // true
 * isDatePast("2026-03-15") // false (today is not "past")
 * isDatePast("2026-03-16") // false
 */
export const isDatePast = (dateString: string): boolean => {
  return daysSinceDate(dateString) > 0;
};

/**
 * Get the number of calendar days between two date-only strings.
 * Timezone-safe — both dates are parsed via parseDateLocal.
 *
 * @param startDate - Start date string (YYYY-MM-DD)
 * @param endDate - End date string (YYYY-MM-DD)
 * @returns Number of days (positive if endDate is after startDate)
 */
export const daysBetweenDates = (startDate: string, endDate: string): number => {
  const start = parseDateLocal(startDate);
  const end = parseDateLocal(endDate);
  // Math.round handles DST transitions where midnight-to-midnight is 23 or 25 hours
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * Format a date string for display without timezone conversion
 * Use this for date-only fields stored as plain date strings in the database
 * 
 * Handles multiple formats:
 * - Plain date: "2025-06-01"
 * - ISO timestamp: "2025-06-01T00:00:00.000Z"
 * - Postgres timestamp: "2025-06-01 00:00:00+00"
 * 
 * @param dateString - Date string in various formats or Date object
 * @returns Formatted date string (e.g., "Jun 1, 2025")
 */
export const formatDate = (dateString: string | undefined | Date): string => {
  if (!dateString) return 'N/A';
  
  // If it's already a Date object, format it directly
  if (dateString instanceof Date) {
    return dateString.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
  
  // Parse as local date to avoid timezone shifts
  // parseDateLocal handles all formats: plain dates, ISO timestamps, and Postgres timestamps
  const date = parseDateLocal(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format a date string as "Month Day" (e.g., "Jun 5")
 * Timezone-safe and standardizes display for billing summaries
 * 
 * @param dateString - Date string or Date object
 * @returns Formatted string (e.g., "Jun 5")
 */
export const formatMonthDay = (dateString: string | undefined | Date): string => {
  if (!dateString) return 'N/A';
  const date = dateString instanceof Date ? dateString : parseDateLocal(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format a date string as "Month Year" (e.g., "June 2025")
 * Timezone-safe and standardizes display for period headers
 * 
 * @param dateString - Date string or Date object
 * @returns Formatted string (e.g., "June 2025")
 */
export const formatMonthYear = (dateString: string | undefined | Date): string => {
  if (!dateString) return 'N/A';
  const date = dateString instanceof Date ? dateString : parseDateLocal(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Format a date for regular people: "Tuesday, January 20, 2026"
 * Standard US long format with weekday.
 */
export const formatDateRegular = (dateString: string | undefined | Date): string => {
  if (!dateString) return 'N/A';
  
  const date = dateString instanceof Date ? dateString : parseDateLocal(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  return date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

export const formatDateTime = (dateString: string | undefined | Date): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format a date and time for regular people: "Tuesday, January 20, 2026 at 3:00 PM"
 */
export const formatDateTimeRegular = (dateString: string | undefined | Date): string => {
  if (!dateString) return 'N/A';
  
  const date = dateString instanceof Date ? dateString : new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const datePart = date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  
  return `${datePart} at ${timePart}`;
};

export const formatTime = (date: Date | string | undefined): string => {
  if (!date) return 'N/A';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

// ============================================================================
// TIMEZONE-AWARE FORMATTERS (use these when a specific IANA timezone is known)
// ============================================================================

type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

/**
 * Extract calendar/time parts for a given instant in a specific timezone.
 * This is the building block for both formatting and "keep wall-clock time" conversions.
 */
export const getZonedDateParts = (date: Date, timeZone: string): ZonedParts => {
  // `formatToParts` is widely supported and avoids brittle string parsing.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    // `hourCycle: 'h23'` NOT `hour12: false`. On Node 18's ICU 74 the latter
    // renders local midnight as hour 24 on the same day; Node 22's ICU 76 says
    // 00. Every caller wants 0-23, and `zonedTimeToUtcDate` below subtracts a
    // whole day when it reads 24, so midnight-anchored conversions came out a
    // day early on CI's engine and were correct on a modern laptop. Measured on
    // v18.20.8 (ICU 74.2) and v22.14.0 (ICU 76.1): with `hourCycle: 'h23'` both
    // return hour 00. Do not "fix" a recurrence of this by bumping Node.
    hourCycle: 'h23',
  }).formatToParts(date);

  const lookup = (type: Intl.DateTimeFormatPartTypes) => {
    const value = parts.find(p => p.type === type)?.value;
    return value ? parseInt(value, 10) : 0;
  };

  return {
    year: lookup('year'),
    month: lookup('month'),
    day: lookup('day'),
    hour: lookup('hour'),
    minute: lookup('minute'),
    second: lookup('second'),
  };
};

/**
 * Convert a *local wall-clock* date/time in a timezone into an actual UTC Date.
 *
 * This is what you want for recurring events that should stay at the same local time
 * across DST changes (e.g., always 9:00 AM America/New_York).
 */
export const zonedTimeToUtcDate = (input: {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second?: number;
}, timeZone: string): Date => {
  const { year, month, day, hour, minute, second = 0 } = input;

  // Start with a UTC guess of the intended wall-clock time.
  let guess = new Date(Date.UTC(year, month - 1, day, hour, minute, second, 0));

  // Adjust based on how the guess renders in the target timezone.
  // Two passes handles DST boundaries reliably enough for scheduling.
  for (let i = 0; i < 2; i++) {
    const actual = getZonedDateParts(guess, timeZone);
    const actualAsUtcMs = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second,
      0
    );
    const intendedAsUtcMs = Date.UTC(year, month - 1, day, hour, minute, second, 0);
    const diffMs = actualAsUtcMs - intendedAsUtcMs;
    if (diffMs === 0) break;
    guess = new Date(guess.getTime() - diffMs);
  }

  return guess;
};

/**
 * Re-anchor a recurring occurrence to the base event's wall-clock time.
 *
 * RRule expands occurrences keeping a *fixed UTC* time-of-day, so any occurrence
 * on the far side of a DST transition lands an hour off in the event's local
 * wall-clock. This takes the occurrence's calendar DATE (read in `timeZone`) and
 * the base event's TIME-OF-DAY (read in `timeZone`) and returns the UTC instant
 * that has the base wall-clock time on the occurrence's date — so e.g. a "6:00 PM
 * America/New_York" series stays 6:00 PM across the November fall-back.
 *
 * When the occurrence and the base sit on the same side of every DST boundary
 * (the common case, and the only case before this matters), the inputs already
 * agree and the result is byte-identical to the raw occurrence instant.
 *
 * Spring-forward gap (documented known-bounded edge, not handled specially):
 * if the base wall-clock time-of-day falls in the one nonexistent hour on
 * spring-forward day (e.g. a 2:30 AM base when clocks jump 2:00→3:00 AM), that
 * wall-clock instant doesn't exist on that date. `zonedTimeToUtcDate`'s two-pass
 * convergence then settles on the nearest real instant (effectively an hour to
 * one side), so that single occurrence can land ~1h off its wall-clock. Impact
 * is negligible for a cheer gym — practices/events are never scheduled at 2:30
 * AM — so we accept this rather than add gap-resolution complexity.
 */
export const reanchorOccurrenceToBaseWallClock = (
  occurrenceInstant: Date,
  baseInstant: Date,
  timeZone: string
): Date => {
  const occParts = getZonedDateParts(occurrenceInstant, timeZone);
  const baseParts = getZonedDateParts(baseInstant, timeZone);

  return zonedTimeToUtcDate(
    {
      year: occParts.year,
      month: occParts.month,
      day: occParts.day,
      hour: baseParts.hour,
      minute: baseParts.minute,
      second: baseParts.second,
    },
    timeZone
  );
};

export const formatTimeInTimeZone = (date: Date | string | undefined, timeZone?: string): string => {
  if (!date) return 'N/A';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!timeZone) return formatTime(dateObj);

  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(dateObj);
  } catch {
    // Invalid timezone string (e.g., "CST", garbage data) — fall back to
    // viewer-local instead of throwing (#1467).
    return formatTime(dateObj);
  }
};

/**
 * #1787 — timezone abbreviation to append after an event time, ONLY when the
 * viewer's browser zone would show a different wall-clock than the event's gym
 * zone (e.g. "CDT" on a Central gym's practice viewed from an Eastern browser).
 * Returns '' when the zones agree at the event's date (compared by short name,
 * so same-offset zones dedupe), when timeZone is missing/invalid, or when the
 * date is missing — the label must never break a time render. Times display in
 * gym time by design (no per-user timezone setting); this just makes that
 * legible to a remote viewer. Mirrors getEventTimezoneSuffix in
 * cheervue-mobile-app/utils/formatters/timezoneUtils.ts.
 */
export const getTimeZoneSuffixForViewer = (
  timeZone: string | null | undefined,
  date: Date | string | undefined,
  viewerTimeZone?: string,
): string => {
  if (!timeZone || !date) return '';
  try {
    const viewerTz = viewerTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!viewerTz || viewerTz === timeZone) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';
    const abbreviationIn = (tz: string) =>
      new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(dateObj)
        .find(part => part.type === 'timeZoneName')?.value ?? '';
    const eventAbbreviation = abbreviationIn(timeZone);
    if (!eventAbbreviation || eventAbbreviation === abbreviationIn(viewerTz)) return '';
    return eventAbbreviation;
  } catch {
    return '';
  }
};

/** #1787 — append the conditional zone abbreviation to an already-formatted time string. */
export const appendTimeZoneSuffix = (
  formattedTime: string,
  timeZone: string | null | undefined,
  date: Date | string | undefined,
  viewerTimeZone?: string,
): string => {
  const suffix = getTimeZoneSuffixForViewer(timeZone, date, viewerTimeZone);
  return suffix ? `${formattedTime} ${suffix}` : formattedTime;
};

export const formatDateWithDayInTimeZone = (date: Date | string | undefined, timeZone?: string): string => {
  if (!date) return 'N/A';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!timeZone) return formatDateWithDay(dateObj);

  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(dateObj);
  } catch {
    // Invalid timezone string — fall back to viewer-local instead of throwing,
    // same #1467 guard as formatTimeInTimeZone above.
    return formatDateWithDay(dateObj);
  }
};

export const formatDateInTimeZone = (date: Date | string | undefined | null, timeZone?: string | null): string => {
  if (!date) return 'N/A';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!timeZone) return formatDate(dateObj);

  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(dateObj);
  } catch {
    // Invalid timezone string — fall back to viewer-local instead of throwing,
    // same #1467 guard as formatTimeInTimeZone above.
    return formatDate(dateObj);
  }
};

export const formatExpirationTime = (dateString: string | undefined | null): string => {
  if (!dateString) return 'Never';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = date.getTime() - now.getTime(); // Note: future date minus current time
  
  // If already expired (negative difference)
  if (diffInMs <= 0) {
    return 'Expired';
  }
  
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);

  if (diffInMinutes < 60) {
    return `in ${diffInMinutes}m`;
  } else if (diffInHours < 24) {
    return `in ${diffInHours}h`;
  } else if (diffInDays < 7) {
    return `in ${diffInDays}d`;
  } else if (diffInWeeks < 4) {
    return `in ${diffInWeeks}w`;
  } else if (diffInMonths < 12) {
    return `in ${diffInMonths}mo`;
  } else {
    const diffInYears = Math.floor(diffInDays / 365);
    return `in ${diffInYears}y`;
  }
};

// ============================================================================
// DURATION & RELATIVE TIME FUNCTIONS
// ============================================================================

/**
 * Calculate the duration between two dates in days and months
 * Uses accurate average month length (365.25 days / 12 months = 30.44 days)
 * 
 * @param startDate - Start date string (YYYY-MM-DD)
 * @param endDate - End date string (YYYY-MM-DD)
 * @returns Object with days and months, or null if invalid dates
 */
export const calculateDuration = (startDate: string, endDate: string): { days: number; months: number } | null => {
  if (!startDate || !endDate) return null;
  
  const start = parseDateLocal(startDate);
  const end = parseDateLocal(endDate);
  
  if (end <= start) return null;
  
  const diffInDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  // Use accurate average month length: 365.25 days / 12 months = 30.44 days per month
  const months = Math.round(diffInDays / 30.44);
  
  return { days: diffInDays, months };
};

/**
 * Calculate the end date for a given start date and duration in weeks
 * 
 * @param startDate - Start date string (YYYY-MM-DD)
 * @param weeks - Number of weeks
 * @returns End date string (YYYY-MM-DD) or null
 */
export const addWeeks = (startDate: string, weeks: number): string | null => {
  if (!startDate || !weeks || weeks <= 0) return null;
  
  const start = parseDateLocal(startDate);
  const end = new Date(start);
  end.setDate(start.getDate() + (weeks * 7));
  
  return formatDateOnly(end);
};

/**
 * Calculate the number of weeks between two dates
 * 
 * @param startDate - Start date string (YYYY-MM-DD)
 * @param endDate - End date string (YYYY-MM-DD)
 * @returns Number of weeks or null
 */
export const calculateWeeks = (startDate: string, endDate: string): number | null => {
  const duration = calculateDuration(startDate, endDate);
  if (!duration) return null;
  
  return Math.round(duration.days / 7);
};

/**
 * Format duration for display
 * 
 * @param startDate - Start date string (YYYY-MM-DD)
 * @param endDate - End date string (YYYY-MM-DD)
 * @returns Formatted string like "333 days (approximately 11 months)"
 */
export const formatDuration = (startDate: string, endDate: string): string => {
  const duration = calculateDuration(startDate, endDate);
  
  if (!duration) return '';
  
  const monthText = duration.months === 1 ? 'month' : 'months';
  return `${duration.days} days (approximately ${duration.months} ${monthText})`;
};

/**
 * Format a Date object or string as MM-DD-YYYY (standard US format)
 * 
 * @param date - Date object or string
 * @returns Date string in MM-DD-YYYY format
 */
export const formatDateUS = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseDateLocal(date) : date;
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const year = dateObj.getFullYear();
  return `${month}-${day}-${year}`;
};

/**
 * Format a time from HH:MM:SS or HH:MM to 12-hour format (h:MM AM/PM)
 * Use this for time-only values stored as plain time strings (not timestamps)
 * 
 * @param time - Time string in HH:MM:SS or HH:MM format (24-hour)
 * @returns Formatted time string in 12-hour format (e.g., "9:00 AM", "2:30 PM")
 * 
 * @example
 * formatTimeTo12Hour("09:00:00") // "9:00 AM"
 * formatTimeTo12Hour("14:30:00") // "2:30 PM"
 * formatTimeTo12Hour("00:00:00") // "12:00 AM"
 */
export const formatTimeTo12Hour = (time: string | null | undefined): string | null => {
  if (!time) return null;
  
  try {
    // Handle HH:MM:SS or HH:MM format
    const timeParts = time.split(':');
    let hours = parseInt(timeParts[0]);
    const minutes = timeParts[1];
    const period = hours >= 12 ? 'PM' : 'AM';
    
    // Convert to 12-hour format
    hours = hours % 12 || 12; // 0 becomes 12
    
    return `${hours}:${minutes} ${period}`;
  } catch (error) {
    return time; // Return original if parsing fails
  }
};

/**
 * Convert time string (HH:MM:SS or HH:MM) to minutes since midnight
 * 
 * @param timeStr - Time string
 * @returns Minutes since midnight
 */
export const timeToMinutes = (timeStr: string | null | undefined): number => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + (minutes || 0);
};

/**
 * Convert minutes since midnight to time string (HH:MM:SS)
 * 
 * @param minutes - Minutes since midnight
 * @returns Time string in HH:MM:SS format
 */
export const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
};

/**
 * Check if two time ranges overlap
 * 
 * @param start1 - First range start (minutes)
 * @param end1 - First range end (minutes)
 * @param start2 - Second range start (minutes)
 * @param end2 - Second range end (minutes)
 * @returns True if ranges overlap
 */
export const timesOverlap = (
  start1: number,
  end1: number,
  start2: number,
  end2: number
): boolean => {
  return start1 < end2 && start2 < end1;
};

/**
 * Format date with day of week (timezone-safe)
 * 
 * Handles multiple formats:
 * - Plain date: "2025-06-01"
 * - ISO timestamp: "2025-06-01T00:00:00.000Z"
 * - Postgres timestamp: "2025-06-01 00:00:00+00"
 * 
 * @param dateString - Date string in various formats or Date object
 * @returns Formatted date with day (e.g., "Mon, Jun 1, 2025")
 */
export const formatDateWithDay = (dateString: string | undefined | Date): string => {
  if (!dateString) return 'N/A';
  
  // If it's already a Date object, format it directly
  if (dateString instanceof Date) {
    return dateString.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }
  
  // Parse as local date to avoid timezone shifts
  // parseDateLocal handles all formats: plain dates, ISO timestamps, and Postgres timestamps
  const date = parseDateLocal(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format a date with its weekday, no year and no comma (timezone-safe).
 *
 * The compact form the session-date chips use on both the public class page and
 * the staff class detail card: "Sat Jun 27". The year is redundant there (the
 * run's start and end are stated above the list) and the comma costs width in a
 * chip that has to wrap a dozen siblings.
 *
 * @param dateString - Date string in any format parseDateLocal accepts
 * @returns Formatted date with weekday (e.g., "Sat Jun 27")
 */
export const formatDateWithDayNoYear = (dateString: string | undefined | Date): string => {
  if (!dateString) return 'N/A';

  const date = dateString instanceof Date ? dateString : parseDateLocal(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';

  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${weekday} ${monthDay}`;
};

/**
 * Format a date range for display (timezone-safe)
 * Handles both single dates and date ranges
 * 
 * @param startDate - Start date string (YYYY-MM-DD)
 * @param endDate - End date string (YYYY-MM-DD), optional
 * @returns Formatted date range string
 * 
 * @example
 * formatDateRange("2025-06-01", "2025-06-05") // "Jun 1 - Jun 5, 2025"
 * formatDateRange("2025-06-01") // "Jun 1, 2025"
 */
export const formatDateRange = (startDate: string | null | undefined, endDate?: string | null): string | null => {
  if (!startDate) return null;
  
  try {
    // Use timezone-safe parsing
    const start = parseDateLocal(startDate);
    const startFormatted = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    if (endDate && endDate !== startDate) {
      const end = parseDateLocal(endDate);
      const endFormatted = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${startFormatted} - ${endFormatted}`;
    }
    
    const year = start.toLocaleDateString('en-US', { year: 'numeric' });
    return `${startFormatted}, ${year}`;
  } catch (error) {
    return startDate;
  }
};

/**
 * Extract year/month/day for a Date as it falls in the given IANA timezone.
 * Falls back to the browser's local timezone when no zone is supplied.
 *
 * Used by formatEventDateRange so the displayed calendar dates reflect the
 * event's saved timezone, not the viewer's UTC clock. Prior to PR fix this
 * was the source of "May 7-9" rendering as "May 7-10" for Central/Pacific
 * gyms, because all-day end_time is stored as 23:59:59 in the event's local
 * zone and crosses into the next UTC day.
 */
const getDatePartsInTimeZone = (
  date: Date,
  timeZone?: string
): { year: number; month: number; day: number } => {
  const fallback = () => ({
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate(),
  });
  if (!timeZone) return fallback();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const pick = (type: 'year' | 'month' | 'day') =>
      Number(parts.find(p => p.type === type)?.value ?? '0');
    return {
      year: pick('year'),
      month: pick('month') - 1, // align with Date's 0-indexed month
      day: pick('day'),
    };
  } catch {
    // Invalid timezone string (e.g., "CST", garbage data) — fall back to
    // viewer-local instead of throwing.
    return fallback();
  }
};

/**
 * Format date range for events, handling both single-day and multi-day events.
 *
 * Reads the start/end dates in the event's stored timezone (when provided) so
 * an all-day event saved in Central Time displays the dates the gym owner
 * actually picked, regardless of the viewer's browser timezone.
 *
 * @param startDate - Start date of the event (Date object or ISO string)
 * @param endDate - End date of the event (optional, Date object or ISO string)
 * @param isAllDay - Whether this is an all-day event
 * @param eventTimezone - IANA timezone the event was created in (e.g., "America/Chicago").
 *                       When omitted, falls back to the viewer's local browser timezone.
 *
 * @example
 * formatEventDateRange(new Date('2025-06-01'), new Date('2025-06-05'), true, 'America/Chicago')
 * // "Jun 2025 1-5 (5 days)"
 */
export const formatEventDateRange = (
  startDate: Date | string,
  endDate?: Date | string | null,
  isAllDay: boolean = false,
  eventTimezone?: string
): string => {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = endDate ? (typeof endDate === 'string' ? new Date(endDate) : endDate) : null;

  const startParts = getDatePartsInTimeZone(start, eventTimezone);
  const endParts = end ? getDatePartsInTimeZone(end, eventTimezone) : null;

  const sameCalendarDay = endParts &&
    startParts.year === endParts.year &&
    startParts.month === endParts.month &&
    startParts.day === endParts.day;

  const isMultiDay = end && !sameCalendarDay && end.getTime() > start.getTime();

  if (isMultiDay && endParts) {
    let diffDays: number;

    if (isAllDay) {
      // All-day events span whole calendar days in the event's timezone.
      // Convert each calendar date to a UTC midnight anchor so DST transitions
      // can't perturb the day count.
      const startAnchor = Date.UTC(startParts.year, startParts.month, startParts.day);
      const endAnchor = Date.UTC(endParts.year, endParts.month, endParts.day);
      diffDays = Math.round((endAnchor - startAnchor) / (1000 * 60 * 60 * 24)) + 1;
    } else {
      const diffTime = end!.getTime() - start.getTime();
      diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    // Same month + year: "Jun 2025 1-5 (5 days)"
    if (startParts.year === endParts.year && startParts.month === endParts.month) {
      const monthYearFormatter = new Intl.DateTimeFormat('en-US', {
        ...(eventTimezone ? { timeZone: eventTimezone } : {}),
        month: 'short',
        year: 'numeric',
      });
      const monthYear = monthYearFormatter.format(start);
      return `${monthYear} ${startParts.day}-${endParts.day}${isAllDay ? ` (${diffDays} days)` : ''}`;
    }

    // Different months/years: "Tue, Jul 30 - Fri, Aug 3, 2024"
    const startFormatted = eventTimezone
      ? formatDateWithDayInTimeZone(start, eventTimezone)
      : formatDateWithDay(start);
    const endFormatted = eventTimezone
      ? formatDateWithDayInTimeZone(end!, eventTimezone)
      : formatDateWithDay(end!);
    return `${startFormatted} - ${endFormatted}${isAllDay ? ` (${diffDays} days)` : ''}`;
  }

  // Single-day event - include day of week
  return eventTimezone
    ? formatDateWithDayInTimeZone(start, eventTimezone)
    : formatDateWithDay(start);
};

/**
 * Common days of month for billing cycles (1st - 28th)
 * We limit to 28 to ensure compatibility with all months including February
 */
export const BILLING_DAY_OPTIONS = Array.from({ length: 28 }, (_, i) => i + 1);

/**
 * Format a number as an ordinal day (1st, 2nd, 3rd, 4th, etc.)
 * 
 * @param day - Day of month (1-31)
 * @returns Ordinal string (e.g., "1st", "2nd", "3rd", "4th")
 */
export const ordinalDay = (day: number): string => {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

/**
 * Format a billing month string as "Mon YYYY" (e.g., "Feb 2026")
 *
 * @param billingMonth - "YYYY-MM" format (e.g., "2026-02")
 * @returns Formatted string (e.g., "Feb 2026")
 */
export const formatBillingMonth = (billingMonth: string): string => {
  if (!billingMonth) return '';
  const [year, month] = billingMonth.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  if (isNaN(date.getTime())) return billingMonth;
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

/**
 * Format an array of specific dates into a date range string.
 * Used for camps/events that span multiple days.
 *
 * @param specificDates - Array of date strings (e.g., ["2026-01-05", "2026-01-06", "2026-01-09"])
 * @returns Formatted range (e.g., "Jan 5 – Jan 9") or single date (e.g., "Jan 5")
 */
export const formatCampDateRange = (specificDates: string[] | null | undefined): string => {
  if (!specificDates?.length) return '';
  const sorted = [...specificDates].sort();
  const first = formatMonthDay(sorted[0]);
  if (sorted.length === 1) return first;
  const last = formatMonthDay(sorted[sorted.length - 1]);
  // En dash, matching the every-day tryout invoice copy shipped in #2139 —
  // this is the single source for "first – last" date-range labels on money
  // surfaces, so the two must not drift to different dash characters.
  return `${first} – ${last}`;
};

/**
 * Convert a Date to a local datetime-local input format string (YYYY-MM-DDTHH:MM).
 * Used for HTML <input type="datetime-local"> which requires local time, not UTC.
 *
 * @example toLocalDatetimeString(new Date()) // "2026-04-03T14:30"
 */
export const toLocalDatetimeString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
};

/** Default response deadline window for team placement reveals (48 hours). */
export const RESPONSE_DEADLINE_MS = 48 * 60 * 60 * 1000;

/**
 * Resolve the effective response deadline for a team athlete.
 * Priority: stored deadline → reveal_email_sent_at + 48h → now + 48h.
 */
export const resolveResponseDeadline = (
  storedDeadline?: string | null,
  revealEmailSentAt?: string | null,
): Date => {
  if (storedDeadline) return new Date(storedDeadline);
  if (revealEmailSentAt) return new Date(new Date(revealEmailSentAt).getTime() + RESPONSE_DEADLINE_MS);
  return new Date(Date.now() + RESPONSE_DEADLINE_MS);
};
 